import { Type } from '@sinclair/typebox';
// M27 — named-model registry + per-purpose assignment contracts (ADM-01/02;
// HUB-082 §M27). API-key material (plaintext OR ciphertext) is deliberately
// ABSENT from every response schema — it never leaves the DB row; only a
// `hasApiKey` presence boolean is exposed.
/**
 * The single runtime home of the provider ids the registry can build a model
 * from. The hub's `buildModelFromRow` switch, the create/update route's
 * validation, and the admin's provider dropdown all read THIS list, so a
 * provider offered in the UI is always one the resolver can actually
 * construct. `provider` is stored verbatim and matched exactly.
 *
 * `openai-compatible` is the generic escape hatch for any vendor speaking the
 * OpenAI wire protocol behind its own host — OpenRouter, z.ai, Groq, Together,
 * a self-hosted vLLM. It is one code path rather than one case per vendor.
 */
export const LLM_PROVIDERS = ['openai', 'anthropic', 'google', 'azure', 'openai-compatible'];
/**
 * Providers whose model cannot be constructed without `baseUrl` — Azure needs
 * the resource endpoint, and an OpenAI-compatible vendor IS its base URL.
 *
 * Enforced when the row is WRITTEN, not only when it is resolved: resolution
 * failures fail soft to the boot model, so a row saved without a URL would
 * look configured in the admin while quietly never being used.
 */
export function requiresBaseUrl(provider) {
    return provider === 'azure' || provider === 'openai-compatible';
}
/**
 * The single runtime home of the 12 LLM purposes. The TypeBox union below, the
 * per-purpose assignment store, and every call-site model resolution consume
 * THIS map — there is no second union or purpose list anywhere. A typo against a
 * member fails type-check. (Verified against the M27 prep doc §3.1 call-site
 * inventory.)
 *
 * `embedding` (HUB-091 §7, M31 task-18) is the one non-chat purpose: its
 * assigned registry row must name an EMBEDDING-capable model, and the resolver
 * builds it via the provider SDK's embedding constructor
 * (`resolveEmbeddingModel`, packages/hub/src/ai/model-resolver.ts) rather than
 * the LanguageModel path every other purpose takes.
 */
export const LLM_PURPOSES = {
    ai_generate: 'ai_generate',
    agent: 'agent',
    planner: 'planner',
    session_title: 'session_title',
    session_summary: 'session_summary',
    memory_extraction: 'memory_extraction',
    dreaming: 'dreaming',
    scheduled_run: 'scheduled_run',
    db_connector_generate: 'db_connector_generate',
    db_connector_decompose: 'db_connector_decompose',
    document_summary: 'document_summary',
    embedding: 'embedding',
};
export const LlmPurposeSchema = Type.Union([
    Type.Literal(LLM_PURPOSES.ai_generate),
    Type.Literal(LLM_PURPOSES.agent),
    Type.Literal(LLM_PURPOSES.planner),
    Type.Literal(LLM_PURPOSES.session_title),
    Type.Literal(LLM_PURPOSES.session_summary),
    Type.Literal(LLM_PURPOSES.memory_extraction),
    Type.Literal(LLM_PURPOSES.dreaming),
    Type.Literal(LLM_PURPOSES.scheduled_run),
    Type.Literal(LLM_PURPOSES.db_connector_generate),
    Type.Literal(LLM_PURPOSES.db_connector_decompose),
    Type.Literal(LLM_PURPOSES.document_summary),
    Type.Literal(LLM_PURPOSES.embedding),
]);
/** Registered model (response) — carries only `hasApiKey`, never the key. */
export const LlmModelSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    internalName: Type.String(),
    provider: Type.String(),
    modelName: Type.String(),
    hasApiKey: Type.Boolean(),
    baseUrl: Type.Union([Type.String(), Type.Null()]),
    isDefault: Type.Boolean(),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
export const LlmModelCreateBody = Type.Object({
    internalName: Type.String({ minLength: 1 }),
    provider: Type.String({ minLength: 1 }),
    modelName: Type.String({ minLength: 1 }),
    apiKey: Type.String({ minLength: 1 }),
    baseUrl: Type.Optional(Type.String({ minLength: 1 })),
    isDefault: Type.Optional(Type.Boolean()),
});
/** List response — an array of registered models (never any key material). */
export const LlmModelListResponseSchema = Type.Array(LlmModelSchema);
/** `:id` path param for the detail / update / delete routes. */
export const LlmModelIdParamsSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
});
export const LlmModelUpdateBody = Type.Object({
    internalName: Type.Optional(Type.String({ minLength: 1 })),
    provider: Type.Optional(Type.String({ minLength: 1 })),
    modelName: Type.Optional(Type.String({ minLength: 1 })),
    apiKey: Type.Optional(Type.String({ minLength: 1 })),
    baseUrl: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    isDefault: Type.Optional(Type.Boolean()),
});
/**
 * POST /llm-models/:id/probe — the outcome of sending a fixed `Hi` prompt to
 * the PROVIDER with the row's stored key, base URL and model name (HUB-114).
 * `ok:false` carries the provider's own rejection (`error`, and `statusCode`
 * when it answered over HTTP) so the admin can see WHICH parameter is wrong;
 * `ok:true` carries the model's real reply in `text` (chat) — an embedding
 * probe has no text, only `kind:'embedding'`.
 */
export const LlmModelProbeResponseSchema = Type.Object({
    ok: Type.Boolean(),
    kind: Type.Union([Type.Literal('chat'), Type.Literal('embedding')]),
    latencyMs: Type.Integer({ minimum: 0 }),
    text: Type.Optional(Type.String()),
    error: Type.Optional(Type.String()),
    statusCode: Type.Optional(Type.Integer()),
});
/** One purpose → model binding (response). */
export const LlmAssignmentSchema = Type.Object({
    purpose: LlmPurposeSchema,
    modelId: Type.String({ format: 'uuid' }),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
// M27 task-03 — per-purpose assignment surface (/llm-assignments; ADM-03/04,
// HUB-082). The GET view lists EVERY purpose with its bound model id or `null`
// (unassigned); the PUT body carries just the target model id.
/** One purpose's current binding — `modelId` is null when unassigned. */
export const LlmAssignmentViewSchema = Type.Object({
    purpose: LlmPurposeSchema,
    modelId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
});
/** GET /llm-assignments — all purposes, each with its model id or null. */
export const LlmAssignmentListResponseSchema = Type.Array(LlmAssignmentViewSchema);
/** `:purpose` path param for PUT /llm-assignments/:purpose. */
export const LlmAssignmentPurposeParamsSchema = Type.Object({
    purpose: LlmPurposeSchema,
});
/** PUT /llm-assignments/:purpose body — bind the purpose to this model. */
export const LlmAssignmentPutBody = Type.Object({
    modelId: Type.String({ format: 'uuid' }),
});
//# sourceMappingURL=llm-models.js.map